import { type FC, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { sentryUtils } from "@/shared/utils/sentry";
import { currencyInput } from "@/shared/utils/currencyInput";
import { resolveBalance } from "@/shared/utils/balanceEstimates";
import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { BalanceEstimateSection } from "@/shared/components/EstateItems/BalanceEstimateSection";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

interface CreditCardManualForm {
  institution: string;
  accountNumber: string;
  balance: string;
  unknownBalance: boolean;
  estimateRange: string;
  statementFile: File | null;
}

const EMPTY_FORM: CreditCardManualForm = {
  institution: "",
  accountNumber: "",
  balance: "",
  unknownBalance: false,
  estimateRange: "",
  statementFile: null,
};

interface FormErrors {
  institution?: string;
}

export const AddCreditCardManual: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    CreditCardManualForm,
    FormErrors
  >(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balanceInput = currencyInput(form.balance, (val) => updateForm({ ...form, balance: val }));

  const handleSubmit = async () => {
    if (isSubmitting || isAddingItem || !estateId) return;

    if (!form.institution.trim()) {
      setFieldErrors({ institution: "Please enter the institution name" });
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;
      if (form.statementFile) {
        sourceDocumentId = await uploadDocumentWithAuth(
          form.statementFile,
          estateId,
          "credit_card_statement",
        );
      }

      await addItem({
        estate_id: estateId,
        category: "Unsecured Debts",
        classification: "debt",
        item_name: form.institution.trim(),
        balance: resolveBalance(form.unknownBalance, form.estimateRange, form.balance),
        is_estimated: form.unknownBalance,
        balance_estimate_range: form.unknownBalance ? form.estimateRange || "unsure" : null,
        metadata: {
          institution: form.institution.trim(),
          ...(form.accountNumber.trim() ? { account_number: form.accountNumber.trim() } : {}),
        },
        source_document_id: sourceDocumentId,
      });

      goToScreen("credit_cards_manage", {
        ...answers,
        credit_cards_review: "found",
        add_credit_cards: "",
      });
    } catch (err) {
      setError("Failed to add credit card. Please try again.");
      sentryUtils.captureError(err, "AddCreditCardManual submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">Tell us about this credit card</h2>
      <p className="mt-2 text-center text-secondary">
        Share what you have. You can always come back to add or edit more.
      </p>
      <div className="mt-8 w-full">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting || isAddingItem}
          error={error}
          onSkip={() =>
            goToScreen("credit_cards_manage", {
              ...answers,
              credit_cards_review: "found",
              add_credit_cards: "",
            })
          }
          skipLabel="I'll do this later"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label required>Institution</Label>
              <Input
                value={form.institution}
                onChange={(e) => updateForm({ ...form, institution: e.target.value })}
                placeholder="e.g., Chase, Fidelity, Vanguard"
                className={fieldErrors.institution ? "border-error" : ""}
              />
              <FieldError message={fieldErrors.institution} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                Account number <span className="text-tertiary">(optional)</span>
              </Label>
              <Input
                value={form.accountNumber}
                onChange={(e) => updateForm({ ...form, accountNumber: e.target.value })}
                placeholder="e.g., 1234567890"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Balance</Label>
              <CurrencyInput
                value={balanceInput.displayValue}
                onChange={balanceInput.handleChange}
                placeholder="0.00"
                disabled={form.unknownBalance}
              />
              <BalanceEstimateSection
                balanceUnknown={form.unknownBalance}
                estimateRange={form.estimateRange}
                onBalanceUnknownChange={(checked) =>
                  updateForm({ ...form, unknownBalance: checked, balance: "", estimateRange: "" })
                }
                onEstimateRangeChange={(range) => updateForm({ ...form, estimateRange: range })}
                firstName={person?.first_name ?? undefined}
              />
            </div>

            <UploadZone
              label="Recent statement"
              labelSuffix="(optional)"
              file={form.statementFile}
              onFileSelect={(file) => updateForm({ ...form, statementFile: file })}
              onFileRemove={() => updateForm({ ...form, statementFile: null })}
            />
          </div>
        </AddItemLayout>
      </div>
    </div>
  );
};
